// Service Identité & NIP (programme NIP mutualisé IDG) : corps des routes de CollectivitésConnect.
// Contrat du service lu dans ÉtatCivil Connect (docs/integration/identite-v1.openapi.yaml, v1.0) sans modification.
// Aucun format de NIP n'est imposé : borne de saisie seulement (64 caractères), le motif officiel éventuel vient
// de la configuration du serveur (CC_NIP_FORMAT). Le NIP n'est jamais renvoyé par ces routes.
import { z } from 'zod';

/** Statuts du contrat v1.0, plus DECEASED annoncé par la v1.1 (décès) : traité en examen humain. */
export const STATUTS_NIP = ['VERIFIED', 'NOT_FOUND', 'MISMATCH', 'PENDING', 'UNAVAILABLE', 'REVOKED', 'MANUAL_REVIEW', 'DECEASED'] as const;
export type StatutNip = (typeof STATUTS_NIP)[number];

/** État du rattachement d'identité d'un dossier côté CollectivitésConnect. */
export const ETATS_IDENTITE_NIP = ['DECLAREE', 'PROPOSEE', 'EN_ATTENTE', 'VERIFIEE', 'LITIGIEUSE', 'REVOQUEE', 'DECEDEE', 'SANS_NIP'] as const;
export type EtatIdentiteNip = (typeof ETATS_IDENTITE_NIP)[number];

export const MOTIFS_LITIGE_NIP = ['erreur_etat_civil', 'contestation', 'doublon', 'usurpation', 'autre'] as const;

/** Attributs que CollectivitésConnect peut lire (le NIP n'en fait jamais partie ; défaut : nom, prénoms, date). */
export const ATTRIBUTS_NIP = ['nom', 'prenoms', 'date_naissance', 'lieu_naissance', 'sexe'] as const;
export type AttributNip = (typeof ATTRIBUTS_NIP)[number];

const schemaDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date attendue au format AAAA-MM-JJ');

export const schemaIdentiteDeclareeNip = z.object({
  nom: z.string().trim().min(1).max(120),
  prenoms: z.string().trim().min(1).max(160),
  date_naissance: schemaDate.optional(),
  sexe: z.enum(['M', 'F']).optional(),
});

/** Vérification du demandeur d'un dossier par un agent. Sans NIP : rapprochement par identité (proposition). */
export const schemaVerificationNipDossier = z.object({
  nip: z.string().trim().min(1).max(64).optional(),
  identite: schemaIdentiteDeclareeNip,
  /** Acte de naissance ancien : écarts d'orthographe attendus, examen humain motivé. */
  acte_ancien: z.boolean().default(false),
  /** Personne présente et document vu : sans ce contrôle, un VERIFIED ne vérifie pas l'identité du dossier. */
  titularite_controlee: z.boolean().default(false),
});
export type VerificationNipDossier = z.infer<typeof schemaVerificationNipDossier>;

export const schemaConfirmationRapprochementNip = z.object({
  titularite_controlee: z.literal(true),
  observations: z.string().trim().max(500).optional(),
});

export const schemaSansNipDossier = z.object({ observations: z.string().trim().max(500).optional() });

export const schemaLitigeNip = z.object({
  motif: z.enum(MOTIFS_LITIGE_NIP),
  observations: z.string().trim().min(3).max(1000),
});

export const schemaInstructionLitigeNip = z.object({
  issue: z.enum(['maintenue', 'levee']),
  observations: z.string().trim().min(3).max(1000),
});

export const schemaJournalNip = z.object({
  finalite: z.string().regex(/^[A-Z][A-Z0-9_]{1,40}$/).optional(),
  depuis: schemaDate.optional(),
  limite: z.coerce.number().int().min(1).max(500).default(100),
});

/** Attributs vérifiés proposés au préremplissage (seuls les attributs autorisés). */
export type PreremplissageNip = Partial<Record<AttributNip, string>>;

export interface ReferenceIdentiteNip {
  etat: EtatIdentiteNip;
  dernier_statut: StatutNip | null;
  acte_ancien: boolean;
  titularite_controlee: boolean;
  motif: string | null;
  ecarts: AttributNip[];
  litige: { motif: string; ouvert_le: string; instruit_le: string | null; issue: string | null } | null;
  verifiee_le: string | null;
  modifiee_le: string;
}

export interface ResultatVerificationNip {
  statut: StatutNip;
  reference: ReferenceIdentiteNip;
  /** Consigne pour l'agent ; jamais affichée telle quelle à l'usager. */
  consigne: string;
  preremplissage: PreremplissageNip;
}

export interface EtatConnexionNip {
  active: boolean;
}

export interface EtatNipUsager {
  connexion_disponible: boolean;
  lie: boolean;
  preremplissage_disponible: boolean;
}
